/**
 * The announcer contract.
 *
 * Screen readers do not notice arbitrary DOM changes. When a validation run
 * finishes, a price refreshes, or a request fails, a sighted user sees it and a
 * screen-reader user hears nothing — unless the text lands in a live region that
 * already existed in the DOM. That is what this provides: two long-lived regions
 * (polite and assertive) plus one function to push text into them.
 *
 * Politeness rules we hold to:
 *   - 'polite'    — results, counts, successful saves. Waits for a pause.
 *   - 'assertive' — errors and anything blocking. Interrupts. Use sparingly:
 *                   an assertive region that fires on every keystroke is unusable.
 *
 * The context and hook live in this .ts file (no components) so React Fast Refresh
 * stays happy — a file that exports both a component and a hook loses HMR state.
 */
import { createContext, useContext } from 'react';

export type Politeness = 'polite' | 'assertive';

export interface AnnouncerApi {
  /** Pushes `message` into the matching live region. Empty strings are ignored. */
  announce: (message: string, politeness?: Politeness) => void;
  /** Clears both regions — e.g. when navigating to a new step. */
  clear: () => void;
}

const noop: AnnouncerApi = {
  announce: () => undefined,
  clear: () => undefined,
};

/**
 * Defaults to a no-op rather than throwing. A missing provider must not crash a
 * screen; it degrades to "no announcement", which the AnnouncerProvider test
 * asserts against so the degradation cannot go unnoticed in the app shell.
 */
export const AnnouncerContext = createContext<AnnouncerApi>(noop);

export function useAnnouncer(): AnnouncerApi {
  return useContext(AnnouncerContext);
}
